export interface Block { x: number; y: number; size: number }
export interface BlockMove { from: Block; to?: Block }

/** Match each destination once. Extra source blocks retire; new capacity comes from splits. */
export function matchBlocks(source: Block[], target: Block[]): BlockMove[] {
  const fragments = source.map(block => ({ ...block }))
  if (!fragments.length) return []
  const center = target.reduce((sum, block) => ({ x: sum.x + block.x, y: sum.y + block.y }), { x: 0, y: 0 })
  center.x /= target.length || 1
  center.y /= target.length || 1
  while (fragments.length < target.length) {
    // Divide a large central block into four contiguous pieces. At t=0 they still
    // occupy exactly the original square; no new pixels appear out of nowhere.
    fragments.sort((a, b) => b.size - a.size
      || Math.hypot(a.x - center.x, a.y - center.y) - Math.hypot(b.x - center.x, b.y - center.y))
    const block = fragments.shift()!
    for (const dx of [-1, 1]) {
      for (const dy of [-1, 1]) fragments.push({
        x: block.x + dx * block.size / 4,
        y: block.y + dy * block.size / 4,
        size: block.size / 2,
      })
    }
  }
  const candidates = fragments.flatMap((from, i) => target.map((to, j) => ({
    i, j, distance: (from.x - to.x) ** 2 + (from.y - to.y) ** 2,
  }))).sort((a, b) => a.distance - b.distance)
  const moves: BlockMove[] = fragments.map(from => ({ from }))
  const used = new Set<number>()
  for (const { i, j } of candidates) {
    if (moves[i].to || used.has(j)) continue
    moves[i].to = target[j]
    used.add(j)
  }
  return moves
}
